import { useEffect, useRef, useState } from "react";
import { useSearchParams, useNavigate, useLocation } from "react-router-dom";
import { capitalizeFirstLetter } from "../../utils/stringUtils";
import {
  Search,
  MapPin,
  ChevronRight,
  ChevronLeft,
  ArrowUpRight,
  Star,
} from "lucide-react";
import HomeSearchPlanner from "../../assets/HomeSearchPlanner.svg";
import HomeSearchPerformer from "../../assets/HomeSearchPerfomer.svg";
import HomeSearchCrew from "../../assets/HomeSearchCrew.svg";
import HomeSearchBlog from "../../assets/HomeSearchBlog.svg";
import { useSearch } from "../../hooks/useSearch";
import { getMediaVariant, handleImageError } from "../../utils/mediaUtils";
import LocationSearch from "../common/LocationSearch";
import RegistrationModal from "../auth/RegistrationModal";
import Statistics from "./components/Statistics/Statistics";

const TABS = [
  { name: "planners", src: HomeSearchPlanner, alt: "planners" },
  { name: "performers", src: HomeSearchPerformer, alt: "performers" },
  { name: "crew", src: HomeSearchCrew, alt: "crew" },
  { name: "blogs", src: HomeSearchBlog, alt: "blogs" },
];

const roleToTab = {
  planner: "planners",
  performer: "performers",
  crew: "crew",
};

const TAB_PAGE_KEY = {
  planners: "plannerPage",
  performers: "performerPage",
  crew: "crewPage",
  blogs: "blogPage",
};

const ORANGE_FILTER =
  "brightness(0) saturate(100%) invert(38%) sepia(96%) saturate(600%) hue-rotate(340deg) brightness(108%)";

const Pagination = ({ section, currentPage, totalPages, onPageChange }) => {
  if (!totalPages || totalPages <= 1) return null;
  return (
    <div className="flex justify-end items-center gap-3 mt-6 pr-1">
      <span className="text-sm text-gray-400">
        Page {currentPage} of {totalPages}
      </span>
      <button
        disabled={currentPage <= 1}
        onClick={() => onPageChange(section, currentPage - 1)}
        className="text-orange-500 disabled:text-gray-300"
      >
        <ChevronLeft size={18} />
      </button>
      <button
        disabled={currentPage >= totalPages}
        onClick={() => onPageChange(section, currentPage + 1)}
        className="text-orange-500 disabled:text-gray-300"
      >
        <ChevronRight size={18} />
      </button>
    </div>
  );
};

const getBestTab = (results) => {
  if (!results) return null;
  const professionalTabs = ["planners", "performers", "crew"];

  // Tier 1: Local Matches (results exist and isFallback is false/falsy)
  let bestTab = null;
  let highestScore = -1;
  for (const t of professionalTabs) {
    const sec = results[t];
    if (sec && Array.isArray(sec.results) && sec.results.length > 0 && !sec.isFallback) {
      const topScore = sec.results[0]?.score || 0;
      if (topScore > highestScore) {
        highestScore = topScore;
        bestTab = t;
      }
    }
  }

  if (bestTab) return bestTab;

  // Tier 2: Fallback Matches in other cities (isFallback === "otherCities")
  for (const t of professionalTabs) {
    const sec = results[t];
    if (sec && Array.isArray(sec.results) && sec.results.length > 0 && sec.isFallback === "otherCities") {
      const topScore = sec.results[0]?.score || 0;
      if (topScore > highestScore) {
        highestScore = topScore;
        bestTab = t;
      }
    }
  }

  // Tier 3: Blogs
  if (!bestTab) {
    const blogSec = results.blogs;
    if (blogSec && Array.isArray(blogSec.results) && blogSec.results.length > 0 && !blogSec.isFallback) {
      bestTab = "blogs";
    }
  }

  return bestTab;
};

const ExploreProfessionals = () => {
  const navigate = useNavigate();
  const locationState = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  // Read committed values from URL
  const urlQuery = searchParams.get("q") || "";
  const urlLocation = searchParams.get("location") || "";
  const urlLocationName = searchParams.get("locationName") || "";
  const urlTab = searchParams.get("tab") || "planners";
  const urlPage = Number(searchParams.get("page")) || 1;

  // Initialize tracking refs:
  // If we have a tab parameter in the URL and did not redirect from the home page,
  // we initialize lastAutoSwitchRef to current URL params to bypass auto-switching.
  // Otherwise, query/location start as null to trigger auto-switching on initial load.
  const isSharedUrl = searchParams.get("tab") && !locationState.state?.fromHome;
  const lastAutoSwitchRef = useRef({
    query: isSharedUrl ? urlQuery : null,
    location: isSharedUrl ? urlLocation : null,
  });

  const lastFetchRef = useRef({
    query: null,
    location: null,
    page: null,
  });

  const forceAutoSwitchRef = useRef(false);

  // Local input state — what the user is currently typing (not yet submitted)
  const [inputQuery, setInputQuery] = useState(urlQuery);
  const [inputLocation, setInputLocation] = useState(urlLocation);
  const [inputLocationName, setInputLocationName] = useState(urlLocationName);
  const [isRegistrationModalOpen, setIsRegistrationModalOpen] = useState(false);

  const searchWrapperRef = useRef(null);

  const {
    query,
    location,
    results,
    resultsQuery,
    resultsLocation,
    suggestions,
    showSuggestions,
    loading,
    pages,
    setQuery,
    setLocation,
    runSearch,
    handleQueryChange,
    selectSuggestion,
    handlePageChange,
    closeSuggestions,
  } = useSearch();

  // When URL params change, sync to hook state and trigger search when parameters change
  useEffect(() => {
    setQuery(urlQuery);
    setLocation(urlLocation);
    setInputQuery(urlQuery);
    setInputLocation(urlLocation);
    setInputLocationName(urlLocationName);

    const searchChanged =
      lastFetchRef.current.query !== urlQuery ||
      lastFetchRef.current.location !== urlLocation;

    const pageChanged =
      lastFetchRef.current.page !== urlPage;

    // We only fetch if query, location, or page changed, OR if results is not loaded yet.
    // If only the tab changes, we do NOT trigger an API request since results already contain all tabs data.
    const shouldFetch = searchChanged || pageChanged || !results;

    if (shouldFetch) {
      lastFetchRef.current = { query: urlQuery, location: urlLocation, page: urlPage };

      const pageNum = Number(searchParams.get("page")) || 1;
      const pk = TAB_PAGE_KEY[urlTab] || "plannerPage";

      runSearch(urlQuery, urlLocation, {
        plannerPage: pk === "plannerPage" ? pageNum : 1,
        performerPage: pk === "performerPage" ? pageNum : 1,
        crewPage: pk === "crewPage" ? pageNum : 1,
        blogPage: pk === "blogPage" ? pageNum : 1,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlQuery, urlLocation, urlLocationName, urlTab, searchParams.get("page")]);

  // Auto-switch based on match priority on new search triggers or query changes
  useEffect(() => {
    if (results && !loading) {
      // Only evaluate auto-switching if the results are fresh for the current search query and location
      const isResultsFresh = resultsQuery === urlQuery && resultsLocation === urlLocation;
      const searchChanged =
        lastAutoSwitchRef.current.query !== urlQuery ||
        lastAutoSwitchRef.current.location !== urlLocation;

      const shouldAutoSwitch =
        isResultsFresh && (searchChanged || forceAutoSwitchRef.current);

      if (shouldAutoSwitch) {
        forceAutoSwitchRef.current = false;
        lastAutoSwitchRef.current = { query: urlQuery, location: urlLocation };

        if (!urlQuery.trim()) {
          return;
        }

        const bestTab = getBestTab(results);

        console.log("Auto-tab switcher selection based on score:", bestTab || "remain on " + urlTab);

        if (bestTab && bestTab !== urlTab) {
          setSearchParams((prev) => {
            const next = new URLSearchParams(prev);
            next.set("tab", bestTab);
            next.set("page", "1");
            return next;
          });
        }
      }
    }
  }, [results, loading, urlQuery, urlLocation, urlTab, setSearchParams]);

  const submitSearch = () => {
    const params = {};
    const qTrim = inputQuery.trim();
    const locTrim = inputLocation.trim();
    const locNameTrim = inputLocationName.trim();
    if (qTrim) params.q = qTrim;
    if (locTrim) params.location = locTrim;
    if (locNameTrim) params.locationName = locNameTrim;
    params.tab = urlTab;
    params.page = "1";

    // Set force switcher to true so it auto-switches to the best tab on search submission
    forceAutoSwitchRef.current = true;
    setSearchParams(params);

    // If search params haven't changed, setSearchParams won't trigger the search useEffect.
    // We force a manual run of the search here to trigger the auto-tab switcher.
    if (urlQuery === qTrim && urlLocation === locTrim) {
      runSearch(qTrim, locTrim, {
        plannerPage: 1,
        performerPage: 1,
        crewPage: 1,
        blogPage: 1,
      });
    }
  };

  const handleTabChange = (tabName) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set("tab", tabName);
      next.set("page", "1");
      return next;
    });
  };

  const onPageChange = (section, pageNum) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set("page", String(pageNum));
      return next;
    });
  };

  const handleSuggestionSelect = (text, role) => {
    // Treat suggestion select as an explicit tab destination, so we set the query ref
    // to match the selected text to prevent the auto-switch effect from overriding the tab.
    lastAutoSwitchRef.current = {
      query: text,
      location: inputLocation.trim(),
    };

    setInputQuery(text);
    const params = {};
    if (text.trim()) params.q = text.trim();
    if (inputLocation.trim()) params.location = inputLocation.trim();
    if (inputLocationName.trim()) params.locationName = inputLocationName.trim();
    const tabName = (role && roleToTab[role]) || urlTab;
    params.tab = tabName;
    params.page = "1";
    setSearchParams(params);
    selectSuggestion(text);
  };

  // Close suggestions on outside click or scroll
  useEffect(() => {
    const clickHandler = (e) => {
      if (searchWrapperRef.current && !searchWrapperRef.current.contains(e.target)) {
        closeSuggestions();
      }
    };
    const scrollHandler = () => closeSuggestions();
    document.addEventListener("mousedown", clickHandler);
    window.addEventListener("scroll", scrollHandler, { passive: true });
    return () => {
      document.removeEventListener("mousedown", clickHandler);
      window.removeEventListener("scroll", scrollHandler);
    };
  }, [closeSuggestions]);

  const sectionData = {
    planners: results?.planners,
    performers: results?.performers,
    crew: results?.crew,
    blogs: results?.blogs,
  };

  return (
    <div className="min-h-[70vh] bg-white font-sans pt-10 px-1 md:px-4 mt-8">
      {/* Header */}
      <div className="text-center mt-6 md:mt-16 mb-10">
        <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3">
          Explore
          <span className="text-orange-500">
            &nbsp;{urlQuery ? `"${urlQuery}"` : ""}{" "}
          </span>
          {urlLocationName ? "in" : ""}
          <div>{urlLocationName ? ` ${urlLocationName}` : ""}</div>
        </h1>
        {!loading && sectionData[urlTab] && sectionData[urlTab].isFallback ? (
          <>
            {sectionData[urlTab].isFallback === "otherCities" ? (
              <p className="text-gray-400 text-sm md:text-md">
                No {urlTab} in{" "}
                <span className="font-medium text-gray-600">{urlLocationName}</span>.{" "}
                Explore from other cities
              </p>
            ) : (
              <p className="text-gray-400 text-sm md:text-md">
                No results for{" "}
                <span className="font-medium text-gray-600">"{urlQuery}"</span>.{" "}
                You might also like these
              </p>
            )}
          </>
        ) : (
          <p className="text-gray-500 text-sm md:text-md">
            Compare professionals, explore ideas, and plan your event with more confidence.
          </p>
        )}
      </div>

      {/* Search bar */}
      <div className="max-w-2xl md:mx-auto mx-1 mb-16 relative" ref={searchWrapperRef}>
        <div className="flex flex-row items-center bg-white border border-gray-200 rounded-lg shadow-sm">
          {/* Location input */}
          <div className="relative flex items-center px-1 md:px-3 w-28 md:w-44 shrink-0 border-r border-gray-100">
            <MapPin size={16} className="text-gray-400 mr-1 shrink-0" />
            <LocationSearch
              value={{ id: inputLocation, name: inputLocationName }}
              onChange={(city) => {
                setInputLocation(city.id || "");
                setInputLocationName(city.name || "");
              }}
              placeholder="Location"
              hideIcon={true}
              inputClassName="outline-none text-sm w-full py-2 md:py-3 border-none bg-transparent"
              className="w-full"
              disableCreate={true}
            />
          </div>

          <div className="w-[1px] h-8 bg-gray-200 shrink-0" />

          {/* Query input */}
          <div className="flex items-center px-2 md:px-3 flex-1 md:flex-[2]">
            <Search size={16} className="text-gray-400 mr-2 shrink-0" />
            <input
              type="text"
              value={inputQuery}
              onChange={(e) => {
                setInputQuery(e.target.value);
                handleQueryChange(e.target.value);
              }}
              onKeyDown={(e) => e.key === "Enter" && submitSearch()}
              placeholder="Search planners, performers, events"
              className="outline-none text-sm w-full py-2 md:py-3"
              autoComplete="off"
            />
          </div>

          <button
            onClick={submitSearch}
            className="bg-[#FF512F] text-white px-4 md:px-7 py-2 md:py-3 min-w-[60px] md:min-w-[90px] rounded-md font-bold text-xs md:text-sm shrink-0 flex items-center justify-center"
          >
            {loading ? (
              <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
            ) : (
              "Search"
            )}
          </button>
        </div>

        {/* Suggestions dropdown */}
        {showSuggestions && suggestions.people.length > 0 && (
          <ul className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-100 rounded-xl shadow-lg z-50 overflow-hidden divide-y divide-gray-50">
            {suggestions.people.map((person) => (
              <li key={person._id}>
                <button
                  onMouseDown={(e) => {
                    e.preventDefault();
                    handleSuggestionSelect(person.companyName, person.professionalInfo?.role);
                  }}
                  className="w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50"
                >
                  {person.companyName}
                  <span className="text-gray-400 text-xs ml-2 capitalize">
                    {person.professionalInfo?.role}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Tabs */}
      <div className="max-w-4xl mx-auto flex overflow-x-auto justify-start md:justify-center gap-3 md:gap-6 mb-8 px-2 md:px-6 no-scrollbar">
        {TABS.map((tab) => (
          <div
            key={tab.name}
            onClick={() => handleTabChange(tab.name)}
            className={`flex flex-col aspect-square items-center justify-center py-4 md:py-8 rounded-xl border-2 cursor-pointer min-w-[100px] md:min-w-[150px] ${urlTab === tab.name ? "border-[#FF512F]" : "border-[#4a4a4a99]"
              }`}
          >
            <div className="mb-2 md:mb-3">
              <img
                src={tab.src}
                alt={tab.alt}
                className="w-8 h-8 md:w-10 md:h-10"
                style={{ filter: urlTab === tab.name ? ORANGE_FILTER : "none" }}
              />
            </div>
            <span
              className={`text-xs md:text-lg font-medium capitalize ${urlTab === tab.name ? "text-[#FF512F]" : "text-gray-900"
                }`}
              style={{ fontFamily: "CabinetGrotesk-Medium" }}
            >
              {tab.name}
            </span>
          </div>
        ))}
      </div>

      <hr className="text-orange-500 max-w-2xl mx-auto mb-10" />

      {/* Results */}
      <div className="max-w-5xl mx-auto md:px-6 mb-20">
        {/* Planners / Performers / Crew */}
        {["planners", "performers", "crew"].includes(urlTab) &&
          (() => {
            const section = sectionData[urlTab];
            const items = section?.results || [];
            const pk = TAB_PAGE_KEY[urlTab];

            return (
              <>
                {loading && (
                  <div className="text-center text-gray-400 py-20">Searching</div>
                )}

                {!loading && items.length === 0 && !results && (
                  <div className="text-center text-gray-400 py-20">
                    Search to find {urlTab}
                  </div>
                )}

                {!loading && section && (
                  <>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-2 md:gap-6">
                      {items.map((org) => (
                        <div
                          key={org._id}
                          onClick={() => navigate(`/profile/${org._id}`)}
                          className="group cursor-pointer shadow-xl border-2 border-gray-200 px-2 py-1 md:px-4 md:py-4 min-h-56 rounded-2xl flex flex-col justify-between"
                        >
                          <div className="relative aspect-[1/1] rounded-xl md:rounded-[20px] overflow-hidden mb-1 md:mb-8">
                            <img
                              src={
                                getMediaVariant(
                                  typeof org.profilePicture === "string"
                                    ? org.profilePicture
                                    : org.profilePicture?.url,
                                  'medium'
                                ) || "https://images.unsplash.com/photo-1501281668745-f7f57925c3b4"
                              }
                              onError={(e) => handleImageError(e, "https://images.unsplash.com/photo-1501281668745-f7f57925c3b4")}
                              className="w-full h-full object-cover"
                              alt={org.companyName}
                            />
                          </div>
                          <div className="flex items-center justify-between gap-1 mt-1">
                            <span
                              className="text-md md:text-2xl truncate"
                              style={{ fontFamily: "CabinetGrotesk-Bold" }}
                            >
                              {capitalizeFirstLetter(org.companyName || org.userInfo?.fullName)}
                            </span>
                            <span className="flex items-center gap-0.5 shrink-0 text-[9px] md:text-xs text-gray-600 whitespace-nowrap">
                              <Star size={10} className="fill-orange-500 text-orange-500 md:hidden" />
                              <Star size={16} className="fill-orange-500 text-orange-500 hidden md:block" />
                              {org.avgRating ?? 0} | {org.reviewCount ?? 0}
                            </span>
                          </div>
                          <p className="text-gray-400 text-[8px] md:text-xs md:mt-1 line-clamp-2 mb-1 md:mb-3">
                            {org.about || "Professional event organizer"}
                          </p>
                          <hr className="text-gray-200 w-full md:mt-1" />
                          <div className="flex flex-row gap-1 mt-1 md:mt-4 mb-1">
                            <button
                              onClick={() => navigate(`/profile/${org._id}`)}
                              className="flex-1 py-1 md:py-2 border text-orange-500 border-orange-500 rounded-md text-[9px] md:text-sm text-center whitespace-nowrap"
                            >
                              View
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                const displayName = capitalizeFirstLetter(org.companyName || org.userInfo?.fullName || "Professional");
                                const role = org.professionalInfo?.role
                                  ? org.professionalInfo.role.charAt(0).toUpperCase() + org.professionalInfo.role.slice(1)
                                  : (urlTab === "performers" ? "Performer" : urlTab === "crew" ? "Crew" : "Planner");
                                const displayLocation = org.locationText || "Location";

                                window.dispatchEvent(new CustomEvent("openEventWizard", {
                                  detail: {
                                    isDirectQuote: true,
                                    professionalDetails: {
                                      name: displayName,
                                      image: typeof org.profilePicture === "string" ? org.profilePicture : org.profilePicture?.url || "",
                                      rating: org.avgRating ?? 0,
                                      reviews: org.reviewCount ?? 0,
                                      role: role,
                                      location: displayLocation.split(",")[0],
                                      since: org.establishmentYear || new Date().getFullYear(),
                                    },
                                    prefill: {
                                      assignedTo: org._id,
                                      hiringRole: role.toLowerCase()
                                    }
                                  }
                                }));
                              }}
                              className="flex-1 py-1 md:py-2 bg-[#FF512F] text-white rounded-md text-[9px] md:text-sm text-center whitespace-nowrap"
                            >
                              Book Now
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>

                    <Pagination
                      section={pk}
                      currentPage={urlPage}
                      totalPages={section.totalPages}
                      onPageChange={onPageChange}
                    />
                  </>
                )}
              </>
            );
          })()}

        {/* Blogs */}
        {urlTab === "blogs" &&
          (() => {
            const section = results?.blogs;
            const items = section?.results || [];

            return (
              <>
                {loading && (
                  <div className="text-center text-gray-400 py-20">Searching</div>
                )}

                {!loading && !results && (
                  <div className="text-center text-gray-400 py-20">
                    Search to find blogs
                  </div>
                )}

                {!loading && section && (
                  <>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-6">
                      {items.map((blog) => (
                        <div
                          key={blog._id}
                          className="group cursor-pointer shadow-xl border-2 border-gray-200 px-2 py-1 md:px-4 md:py-4 min-h-56 rounded-2xl"
                          onClick={() => navigate(`/blogs/${blog.slug}`)}
                        >
                          <div className="relative aspect-square rounded-xl md:rounded-[20px] overflow-hidden mb-3 md:mb-8">
                            <img
                              src={blog.mainImage}
                              alt={blog.title}
                              className="w-full h-full object-cover"
                            />
                          </div>
                          <div className="flex items-start justify-between gap-2">
                            <h3
                              className="text-[15px] md:text-xl line-clamp-2"
                              style={{ fontFamily: "CabinetGrotesk-Bold" }}
                            >
                              {blog.title}
                            </h3>
                            <ArrowUpRight
                              size={24}
                              className="text-orange-500 shrink-0 mt-0.5 hidden md:block"
                            />
                            <ArrowUpRight
                              size={20}
                              className="text-orange-500 shrink-0 mt-1 md:hidden"
                            />
                          </div>
                          <p className="text-gray-500 text-[10px] md:text-xs mt-1 line-clamp-2">
                            {blog.intro}
                          </p>
                          <div className="flex flex-wrap gap-2 mt-2">
                            {blog.tags?.slice(0, 2).map((tag, i) => (
                              <span
                                key={i}
                                className="md:text-sm text-[10px] text-orange-500 px-1 py-1 md:px-2 md:py-[2px] bg-white min-w-1 border border-orange-500 rounded-lg"
                              >
                                {tag}
                              </span>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>

                    <Pagination
                      section="blogPage"
                      currentPage={urlPage}
                      totalPages={section.totalPages}
                      onPageChange={onPageChange}
                    />
                  </>
                )}
              </>
            );
          })()}
      </div>

      {isRegistrationModalOpen && (
        <RegistrationModal onClose={() => setIsRegistrationModalOpen(false)} />
      )}

      <Statistics />
    </div>
  );
};

export default ExploreProfessionals;
